import React, { Fragment, FunctionComponent, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  StepperContent,
  StepperHeader,
  StepperSubmit
} from '../../../../../shared/general/stepper';
import { utilsHomeValueGetDefaultValuesStep3 } from '../../utils';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorsHomeValueFormFields,
  CalculatorsHomeValueResult,
  CalculatorsHomeValueStep3FormFields
} from '../../../../../../@types/features/calculators';
import { useFormContext } from '../../../../../../hooks';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCalculatorHomeValueStep3FormFields } from '../../config';
import { HomeValueContext } from '../../context';
import { ApiResidenceValuePropertyInformation } from '../../../../../../@types';
import { Heading } from '@chakra-ui/react';
import { validationMessagesTranslated } from '../../../../../shared/validation/validation-messages';

export const CalculatorHomeValueStepperStep3: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const calculatorHomeValueStep3FormFields = useCalculatorHomeValueStep3FormFields(intl);
  const [completedText, setCompletedText] = useState('');
  //const { getPropertyInformation } = homeValueUseResidenceValueHook();
  const { form, updateForm } = useFormContext<
    CalculatorsHomeValueResult,
    CalculatorsHomeValueFormFields
  >();
  const { propertyInformation } = useContext(HomeValueContext);
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsHomeValueGetDefaultValuesStep3(intl)
  });

  const calculateVolume = (propertyInformation: ApiResidenceValuePropertyInformation) => {
    const mapping = [
      { startYear: 0, endYear: 1920, factor: 2 },
      { startYear: 1920, endYear: 1960, factor: 2.2 },
      { startYear: 1960, endYear: 1980, factor: 2.6 },
      { startYear: 1980, endYear: 2003, factor: 2.4 },
      { startYear: 2003, endYear: new Date().getFullYear() + 1, factor: 2.7 }
    ];

    const { buildYear, useSurface } = propertyInformation;

    if (buildYear && useSurface) {
      const factor = mapping.find((m) => {
        return buildYear >= m.startYear && buildYear <= m.endYear;
      });
      if (factor) {
        return Math.round(useSurface * factor.factor);
      }
    }

    return '';
  };

  useEffect(() => {
    if (propertyInformation) {
      if (propertyInformation?.buildYear) {
        setValue('buildYear', propertyInformation?.buildYear?.toString(), {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true
        });
      }

      if (propertyInformation?.useSurface) {
        setValue('useSurface', propertyInformation?.useSurface?.toString(), {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true
        });
      }

      if (propertyInformation?.volume) {
        const volume = propertyInformation?.volume
          ? propertyInformation?.volume
          : calculateVolume(propertyInformation);

        setValue('volume', volume?.toString(), {
          shouldValidate: true,
          shouldTouch: true,
          shouldDirty: true
        });
      }

      setValue('plotSize', propertyInformation?.plotSize || 0, {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true
      });
    }
  }, [propertyInformation]);

  useEffect(() => {
    //console.log('form.residenceType', form.residenceType);
    if (form.residenceType === 'Appartement') {
      setValue('plotSize', '');
    }
  }, [form.residenceType]);

  /*useEffect(() => {
    console.log('isValid', isValid);
    console.log('isDirty', isDirty);
    console.log('errors', errors);
  }, [isValid, isDirty, errors]);*/
  const onSubmit = async (formData: Record<keyof CalculatorsHomeValueStep3FormFields, string>) => {
    setCompletedText(`${formData.buildYear}`);
    updateForm(formData);
  };

  return (
    <Fragment>
      <StepperHeader
        onClose={handleSubmit(onSubmit)}
        title={title}
        id={id}
        completedText={completedText}
      />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={`mb-5`}>
            <Heading size={`md`}>
              <FormattedMessage
                id={`step3.subTitle`}
                defaultMessage={`Wanneer is de woning gebouwd?`}
              />
            </Heading>
            <FormElementDynamicInputTyped<CalculatorsHomeValueStep3FormFields>
              formFields={calculatorHomeValueStep3FormFields}
              id={'buildYear'}
              register={register}
              errors={errors}
              values={watch()}
              validation={{ required: validationMessagesTranslated(intl).required }}
            />
          </div>
          <div>
            <Heading size={`md`}>
              <FormattedMessage
                id={`step3.size.title`}
                defaultMessage={`Hoe groot is de woning?`}
              />
            </Heading>
            <div className={`md:flex md:gap-10`}>
              <FormElementDynamicInputTyped<CalculatorsHomeValueStep3FormFields>
                formFields={calculatorHomeValueStep3FormFields}
                id={'useSurface'}
                register={register}
                errors={errors}
                values={watch()}
                validation={{ required: validationMessagesTranslated(intl).required }}
              />

              <FormElementDynamicInputTyped<CalculatorsHomeValueStep3FormFields>
                formFields={calculatorHomeValueStep3FormFields}
                id={'volume'}
                register={register}
                errors={errors}
                values={watch()}
                validation={{ required: validationMessagesTranslated(intl).required }}
              />
            </div>
          </div>

          {form.residenceType !== 'Appartement' && (
            <div className={`md:flex`}>
              <div className={`md:w-1/2`}>
                <FormElementDynamicInputTyped<CalculatorsHomeValueStep3FormFields>
                  formFields={calculatorHomeValueStep3FormFields}
                  id={'plotSize'}
                  register={register}
                  errors={errors}
                  values={watch()}
                  validation={{ required: validationMessagesTranslated(intl).required }}
                />
              </div>
            </div>
          )}

          <StepperSubmit id={id} isValidated={isValid} />
        </form>
      </StepperContent>
    </Fragment>
  );
};
